import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { select, Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { AppState } from 'src/app/app.reducer';
import { Banco } from 'src/app/dashboard/interfaces/banco';
import { Pais } from 'src/app/dashboard/interfaces/country.interface';
import { CuentaBeneficiario } from 'src/app/dashboard/interfaces/cuenta-beneficiario.interface';
import { TipoCuenta } from 'src/app/dashboard/interfaces/tipo-cuenta.interface';
import { AccountRecipientService } from 'src/app/dashboard/services/account-recipient.service';
import { AccountTypeService } from 'src/app/dashboard/services/account-type.service';
import { BanksService } from 'src/app/dashboard/services/banks.service';
import { CountriesService } from 'src/app/dashboard/services/countries.service';
import { ParameterService } from 'src/app/dashboard/services/parameter.service';
import {
  ParametrosEnum,
  PAGO_MOVIL_MONTO_MAX_DEFAULT,
} from 'src/app/shared/enums/parameters.enum';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import {
  limpiarCuentaVe,
  limpiarDocumentoVe,
  limpiarNombreVe,
  limpiarTelefonoVe,
} from 'src/app/dashboard/utils/limpiadores-beneficiario-ve.util';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-add-account',
  templateUrl: './add-account.component.html',
  styleUrls: ['./add-account.component.sass'],
})
export default class AddAccountComponent implements OnInit, OnDestroy {
  formCuenta: FormGroup | undefined;
  paisesActivos: Pais[] = [];
  tiposID = ['Cédula de identidad nacional', 'Cédula de identidad extranjera'];
  tiposIDJuridica = ['RIF'];
  selectedTipoID: string = '';
  documentPrefix: string = '';
  tiposCuentas: TipoCuenta[] = [];
  bancos: Banco[] = [];
  usuarioID: number | undefined;
  subscriptions: Subscription = new Subscription();
  esUpdate: boolean = false;
  tipoBeneficiario: number = 0;
  formaEnvio: number = 0;
  viewForm: boolean = false;
  isPersonMovilPay: boolean = false;
  isVeUser: boolean = false;
  pagoMovilGlobal: number = 0;
  pagoMovilBanesco: number = 0;
  // Límite máximo (Bs) para Pago Móvil, configurable desde admin (PAGO_MOVIL_MONTO_MAX).
  pagoMovilMontoMax: number = PAGO_MOVIL_MONTO_MAX_DEFAULT;
  // Monto (Bs) de la operación en curso; solo llega cuando el diálogo se abre
  // desde una calculadora. null = sin contexto de monto → sin restricción.
  montoBs: number | null = null;
  /**
   * Por qué no se guardó el último intento. Queda dentro del diálogo, no en un
   * Swal, para que el cliente lo tenga a la vista mientras corrige.
   */
  mensajeError: string | null = null;

  constructor(
    private fb: FormBuilder,
    private countriesService: CountriesService,
    private bancoService: BanksService,
    private tipoCuentaService: AccountTypeService,
    private accountRecipientService: AccountRecipientService,
    private store: Store<AppState>,
    private spinner: SpinnerService,
    private snackBar: MatSnackBar,
    private parameterService: ParameterService,
    public dialogRef: MatDialogRef<AddAccountComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.esUpdate = this.data.type === 'UPDATE';
    this.isVeUser = !!this.data.isVeUser;
    this.montoBs =
      typeof this.data.montoBs === 'number' ? this.data.montoBs : null;
    this.initFormCuenta();
  }

  ngOnInit() {
    this.viewForm = Boolean(this.esUpdate);
    this.obtenerParametrosPagoMovil();
  }

  /**
   * Carga el estado de Pago Móvil (Global y Banesco) desde parámetros.
   * Los mismos parámetros que consume el flujo de envío (fiat-calculator).
   */
  async obtenerParametrosPagoMovil(): Promise<void> {
    try {
      const [globalParam, banescoParam] = await Promise.all([
        this.parameterService.getParameter(ParametrosEnum.PAGO_MOVIL_GLOBAL),
        this.parameterService.getParameter(ParametrosEnum.PAGO_MOVIL_BANESCO),
      ]);
      this.pagoMovilGlobal = Number(globalParam?.valor) || 0;
      this.pagoMovilBanesco = Number(banescoParam?.valor) || 0;
    } catch (error) {
      this.pagoMovilGlobal = 0;
      this.pagoMovilBanesco = 0;
    }

    // Límite de monto para Pago Móvil (maneja su propio default ante errores)
    this.pagoMovilMontoMax = await this.parameterService.getPagoMovilMontoMax();
  }

  /**
   * Pago Móvil no puede usarse como forma de envío cuando la operación en curso
   * supera el límite (en Bs). Sin contexto de monto (montoBs null) no restringe.
   */
  get pagoMovilBloqueadoPorMonto(): boolean {
    return this.montoBs !== null && this.montoBs > this.pagoMovilMontoMax;
  }

  /**
   * Mensaje de advertencia según el estado configurado en el administrador.
   * Devuelve null cuando Pago Móvil está totalmente disponible.
   *   Global=1, Banesco=1 → sin mensaje
   *   Global=0, Banesco=1 → solo Banesco
   *   resto              → no disponible
   */
  get mensajePagoMovil(): string | null {
    if (this.pagoMovilGlobal === 1 && this.pagoMovilBanesco === 1) {
      return null;
    }
    if (this.pagoMovilGlobal === 0 && this.pagoMovilBanesco === 1) {
      return 'Solo Pago Móvil para Banesco por el momento.';
    }
    return 'Pago Móvil no está disponible por el momento. Por favor selecciona otro método de pago.';
  }

  onNoClick(): void {
    this.dialogRef.close(false);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onTipoBeneficiarioChange(value: number): void {
    this.tipoBeneficiario = value;
  }

  onFormaEnvioChange(value: number): void {
    this.formaEnvio = value;
  }

  generateForm() {
    // Defensa: no permitir avanzar a un beneficiario Pago Móvil si el monto supera el límite
    if (this.formaEnvio === 1 && this.pagoMovilBloqueadoPorMonto) {
      return;
    }
    this.viewForm = true;
    this.selectForm();
  }


  async initFormCuenta() {
    if (this.data.userID) {
      this.usuarioID = this.data.userID;
    } else {
      this.subscriptions.add(
        this.store.select('auth').subscribe(({ user }) => {
          this.usuarioID = user?.usuarioId;
        })
      );
    }

    if (this.esUpdate) {
      this.tipoBeneficiario = this.data.cuenta.tipoBeneficiario.tipoBeneficiarioId;
      const prefijo = this.data.cuenta.tipoID;

      if (this.tipoBeneficiario === 0) {
        this.selectedTipoID = prefijo === 'V' ? 'Cédula de identidad nacional' : 'Cédula de identidad extranjera';
      } else {
        this.selectedTipoID = 'RIF';
      }
      this.formCuenta?.get('tipoID')?.setValue(this.selectedTipoID);
      this.onTipoIDChange();
      this.formaEnvio = this.data.cuenta.movilPay;
      this.selectForm();
    }
  }

  async initLists() {
    this.paisesActivos = await this.countriesService.getActiveCountries();
    this.esUpdate ? await this.loadBankData() : await this.setDefaultCountry();
  }

  private async loadBankData() {
    const { banco } = this.data.cuenta;
    const { codigoPais, bancoID } = banco;

    [this.bancos, this.tiposCuentas] = await Promise.all([
      (await this.bancoService.getBanksByCountry(codigoPais)).filter(b => b.codigoBanco !== 'BINANCE'),
      this.tipoCuentaService.getAccountTypes(codigoPais),
    ]);
    this.filterAccountTypesForVe();

    const selectedBank = this.bancos.find(b => b.bancoID === bancoID);
    if (selectedBank) {
      this.formCuenta?.get('banco')?.patchValue(selectedBank);
    }
  }


  private async setDefaultCountry() {
    const venezuela = this.paisesActivos.find(pais => pais.codigoPais === 'VE');
    if (venezuela) {
      this.formCuenta?.get('pais')?.setValue(venezuela.codigoPais);
      this.formCuenta?.get('pais')?.disable();
      await this.getBanks();
    }
  }

  async getBanks() {
    const paisSeleccionado = this.formCuenta?.get('pais')?.value;
    if (!paisSeleccionado) return;
    [this.bancos, this.tiposCuentas] = await Promise.all([
      (await this.bancoService.getBanksByCountry(paisSeleccionado)).filter(res => res.codigoBanco !== 'BINANCE'),
      this.tipoCuentaService.getAccountTypes(paisSeleccionado),
    ]);
    this.filterAccountTypesForVe();
    this.formCuenta?.get('banco')?.setValue('');
    this.formCuenta?.get('tipoCuenta')?.setValue('');
  }

  /**
   * Para usuarios VE persona: solo muestra tipos de cuenta Ahorro y Corriente.
   */
  private filterAccountTypesForVe(): void {
    if (!this.isVeUser || this.tipoBeneficiario !== 0) return;
    this.tiposCuentas = this.tiposCuentas.filter((tc) => {
      const nombre = tc.nombre.toLowerCase();
      return nombre.includes('ahorro') || nombre.includes('corriente');
    });
  }


  onTipoIDChange() {
    this.documentPrefix = '';
    const tipo = this.selectedTipoID.trim();
    if (this.tipoBeneficiario === 0) {
      if (tipo === 'Cédula de identidad nacional') {
        this.documentPrefix = 'V';
      } else {
        this.documentPrefix = 'E';
      }
    }
    if (this.tipoBeneficiario === 1) {
      this.documentPrefix = 'J';
    }
  }

  onBancoChange(banco: Banco): void {
    // El cliente ingresa los 20 dígitos completos (incluido el prefijo del banco)
    // manualmente, por lo que no se autocompleta el prefijo al seleccionar el banco.
  }

  /**
   * Cada campo limpia lo que se escribe o pega, en vez de bloquear teclas.
   * Se engancha a `input` (llega igual al escribir, pegar o autocompletar, en
   * cualquier navegador) y a `compositionend`.
   *
   * Mientras hay una composición abierta —la tilde con tecla muerta en Mac, el
   * autocorrector de iOS— no se toca el valor: reescribirlo a mitad la rompe y
   * deja letras duplicadas. Safari dispara el último `input` antes del
   * `compositionend`, por eso se limpia también ahí.
   */
  limpiarNombre(event: Event, campo: 'nombre' | 'apellidos'): void {
    const admiteNumeros = this.tipoBeneficiario === 1;
    this.aplicarLimpieza(event, campo, (v) => limpiarNombreVe(v, admiteNumeros));
  }

  limpiarDocumento(event: Event): void {
    // Beneficiario empresa = RIF: sus ceros iniciales son parte del número.
    const esRif = this.tipoBeneficiario === 1;
    const letra = this.aplicarLimpieza(
      event,
      'numeroID',
      (v) => limpiarDocumentoVe(v, esRif).numero,
      (v) => limpiarDocumentoVe(v, esRif).letra,
    );
    if (letra) {
      this.elegirTipoDocumento(letra);
    }
  }

  limpiarNumeroCuenta(event: Event): void {
    // Pago Móvil guarda el teléfono en el mismo control que la cuenta.
    this.aplicarLimpieza(
      event,
      'numeroCuenta',
      this.isPersonMovilPay ? limpiarTelefonoVe : limpiarCuentaVe,
    );
  }

  /**
   * Escribe en el control el valor limpio y deja el cursor donde estaba
   * respecto de lo que se conservó. Devuelve lo que extraiga `extra` del
   * valor crudo (la letra del documento), o null si no hubo que limpiar.
   */
  private aplicarLimpieza<T>(
    event: Event,
    campo: string,
    limpiar: (valor: string) => string,
    extra?: (valor: string) => T,
  ): T | null {
    if ((event as InputEvent).isComposing) return null;
    const input = event.target as HTMLInputElement;
    const crudo = input.value;
    const limpio = limpiar(crudo);
    if (limpio === crudo) return null;

    const cursor = input.selectionStart ?? crudo.length;
    const nuevoCursor = Math.min(limpiar(crudo.slice(0, cursor)).length, limpio.length);
    this.formCuenta?.get(campo)?.setValue(limpio);
    input.value = limpio;
    if (document.activeElement === input) {
      input.setSelectionRange(nuevoCursor, nuevoCursor);
    }
    return extra ? extra(crudo) : null;
  }

  /** Si pegaron «V-12.345.678», el tipo de documento se elige solo. */
  private elegirTipoDocumento(letra: 'V' | 'E' | 'J'): void {
    let tipo: string | null = null;
    if (this.tipoBeneficiario === 0 && letra === 'V') tipo = 'Cédula de identidad nacional';
    if (this.tipoBeneficiario === 0 && letra === 'E') tipo = 'Cédula de identidad extranjera';
    if (this.tipoBeneficiario === 1 && letra === 'J') tipo = 'RIF';
    if (!tipo) return;
    this.selectedTipoID = tipo;
    this.formCuenta?.get('tipoID')?.setValue(tipo);
    this.onTipoIDChange();
  }



  async guardarBeneficiario() {
    this.mensajeError = null;

    if (this.formCuenta?.invalid) {
      // Sin esto los campos siguen `untouched` y Material no pinta ningún
      // error: el clic en Guardar parecía no hacer nada.
      this.formCuenta.markAllAsTouched();
      return;
    }
    this.formCuenta?.patchValue({ tipoID: this.documentPrefix });
    let formValue = this.formCuenta?.getRawValue();
    let tipoCuentaID = formValue.tipoCuenta;
    let tipoBeneficiario = this.tipoBeneficiario;
    let pagoMovil = this.formaEnvio;

    // Defensa: bloquear guardar un beneficiario Pago Móvil si el monto supera el límite
    if (pagoMovil === 1 && this.pagoMovilBloqueadoPorMonto) {
      return;
    }

    if (pagoMovil === 1) {
      tipoCuentaID = 1;
    }

    const cuenta: CuentaBeneficiario = {
      ...formValue,
      bancoID: formValue.banco.bancoID,
      tipoCuentaID: tipoCuentaID,
      usuarioID: this.usuarioID,
      tipoBeneficiario: { tipoBeneficiarioId: tipoBeneficiario },
      movilPay: pagoMovil,
      tipoID: formValue.tipoID,
    };



    if (this.esUpdate) {
      cuenta['cuentaBeneficiarioID'] = this.data.cuenta.cuentaBeneficiarioID;
      delete cuenta.banco;
      delete cuenta.tipoCuenta;
    }
    const ref = this.spinner.start();
    try {
      const resultado = await this.accountRecipientService.saveRecipientAccount(
        cuenta
      );
      if (this.esUpdate) {
        Swal.fire({
          icon: 'success',
          title: 'Beneficiario editado',
          text: 'Su Beneficiario fue editado con éxito',
          showCancelButton: false,
          showDenyButton: false,
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#1e5a3f',

        });
      } else {
        Swal.fire({
          icon: 'success',
          title: 'Beneficiario creado',
          text: 'Su Beneficiario fue registrado con éxito',
          showCancelButton: false,
          showDenyButton: false,
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#1e5a3f',

        });
      }


      this.dialogRef.close(true);
    } catch (error) {
      // Antes este `catch` estaba vacío: un duplicado, el bloqueo por
      // actualización de datos o la red caída dejaban el diálogo igual, sin
      // éxito ni error, y el cliente creía que el botón no funcionaba.
      this.mensajeError = this.describirErrorGuardado(error);
    }
    this.spinner.stop(ref);
  }

  /**
   * Traduce lo que rebotó del backend. El `ErrorInterceptor` deja intactos los
   * 400/422/429 (ahí viene el mensaje del backend) y aplana el resto a un
   * string; el `DataUpdateBlockInterceptor` corta antes con `{ status: 423 }`.
   */
  private describirErrorGuardado(error: any): string {
    const generico =
      'No pudimos guardar el beneficiario. Intenta de nuevo en un momento y, ' +
      'si sigue igual, escríbenos a soporte.';

    if (typeof error === 'string') {
      return error || generico;
    }

    const status = error?.status;
    if (status === 423 || error?.error?.code === 'USER_BLOCKED_BY_PENDING_UPDATE') {
      return 'Tienes una actualización de datos pendiente. Complétala desde el inicio para volver a registrar beneficiarios.';
    }
    if (status === 401) {
      return 'Tu sesión venció. Vuelve a iniciar sesión e intenta de nuevo.';
    }
    if (status === 0) {
      return 'No pudimos conectarnos. Revisa tu conexión e intenta de nuevo.';
    }

    const delBackend = error?.error?.message;
    if (typeof delBackend === 'string' && delBackend.trim()) {
      return delBackend;
    }
    return generico;
  }

  cancel() {
    this.dialogRef.close();
  }

  back() {
    this.viewForm = false;
  }

  async selectForm() {

    switch (`${this.tipoBeneficiario}-${this.formaEnvio}`) {
      case '0-0':
        return this.getFormPersonBank();
      case '0-1':
        return this.getFormPersonMovilPay();
      case '1-0':
        return this.getFormCompanyBank();
      case '1-1':
        return this.getFormCompanyMovilPay();
      default:
    }
  }

  async getFormPersonBank() {
    this.isPersonMovilPay = false;
    this.formCuenta = this.fb.group({
      pais: new FormControl(this.esUpdate ? this.data.cuenta.banco.codigoPais : '', [Validators.required]),
      nombre: new FormControl(this.esUpdate ? this.data.cuenta.nombre : '', [Validators.required, Validators.minLength(3), Validators.maxLength(40), Validators.pattern(/^[A-Za-zÁÉÍÓÚáéíóúñÑ\s]*$/)]),
      apellidos: new FormControl(this.esUpdate ? this.data.cuenta.apellidos : '', [Validators.required, Validators.minLength(3), Validators.maxLength(40), Validators.pattern(/^[A-Za-zÁÉÍÓÚáéíóúñÑ\s]*$/)]),
      tipoID: new FormControl(this.esUpdate ? this.data.cuenta.tipoID : '', [Validators.required]),
      numeroID: new FormControl(this.esUpdate ? this.data.cuenta.numeroID : '', [
        Validators.required,
        Validators.maxLength(15),
        Validators.minLength(7),
        Validators.pattern(/^[0-9]+$/)
      ]),
      banco: new FormControl(this.esUpdate ? this.data.cuenta.banco : '', [Validators.required]),
      tipoCuenta: new FormControl(this.esUpdate ? this.data.cuenta.tipoCuentaID : '', [Validators.required]),
      numeroCuenta: new FormControl(this.esUpdate ? this.data.cuenta.numeroCuenta : '', [
        Validators.required,
        Validators.maxLength(20),
        Validators.minLength(20),
        Validators.pattern(/^[0-9]+$/)
      ]),
    });
    this.initLists();
  }

  async getFormPersonMovilPay() {
    this.isPersonMovilPay = true;
    this.formCuenta = this.fb.group({
      pais: new FormControl(this.esUpdate ? this.data.cuenta.banco.codigoPais : '', [Validators.required]),
      nombre: new FormControl(this.esUpdate ? this.data.cuenta.nombre : '', [Validators.required, Validators.minLength(3), Validators.maxLength(40), Validators.pattern(/^[A-Za-zÁÉÍÓÚáéíóúñÑ\s]*$/)]),
      apellidos: new FormControl(this.esUpdate ? this.data.cuenta.apellidos : '', [Validators.required, Validators.minLength(3), Validators.maxLength(40), Validators.pattern(/^[A-Za-zÁÉÍÓÚáéíóúñÑ\s]*$/)]),
      tipoID: new FormControl(this.esUpdate ? this.data.cuenta.tipoID : '', [Validators.required]),
      numeroID: new FormControl(this.esUpdate ? this.data.cuenta.numeroID : '', [Validators.required, Validators.maxLength(15), Validators.minLength(7), Validators.pattern(/^[0-9]*$/)]),
      banco: new FormControl(this.esUpdate ? this.data.cuenta.banco : '', [Validators.required]),
      tipoCuenta: new FormControl(1),
      numeroCuenta: new FormControl(this.esUpdate ? this.data.cuenta.numeroCuenta : '', [
        Validators.required,
        Validators.maxLength(10),
        Validators.minLength(10),
        Validators.pattern(/^[0-9]+$/)
      ]),
    });
    this.initLists();
  }

  async getFormCompanyBank() {
    this.isPersonMovilPay = false;
    this.formCuenta = this.fb.group({
      pais: new FormControl(this.esUpdate ? this.data.cuenta.banco.codigoPais : '', [Validators.required]),
      nombre: new FormControl(this.esUpdate ? this.data.cuenta.nombre : '', [Validators.required, Validators.minLength(3), Validators.maxLength(40), Validators.pattern(/^[A-Za-zÁÉÍÓÚáéíóúñÑ0-9\s]*$/)]),
      tipoID: new FormControl(this.esUpdate ? this.data.cuenta.tipoID : '', [Validators.required]),
      numeroID: new FormControl(this.esUpdate ? this.data.cuenta.numeroID : '', [Validators.required, Validators.maxLength(15), Validators.minLength(7), Validators.pattern(/^[0-9]*$/)]),
      banco: new FormControl(this.esUpdate ? this.data.cuenta.banco : '', [Validators.required]),
      tipoCuenta: new FormControl(this.esUpdate ? this.data.cuenta.tipoCuentaID : '', [Validators.required]),
      numeroCuenta: new FormControl(this.esUpdate ? this.data.cuenta.numeroCuenta : '', [
        Validators.required,
        Validators.maxLength(20),
        Validators.minLength(20),
        Validators.pattern(/^[0-9]*$/),
      ]),
    });
    this.initLists();
  }

  async getFormCompanyMovilPay() {

    this.isPersonMovilPay = true;
    this.formCuenta = this.fb.group({
      pais: new FormControl(this.esUpdate ? this.data.cuenta.banco.codigoPais : '', [Validators.required]),
      nombre: new FormControl(this.esUpdate ? this.data.cuenta.nombre : '', [Validators.required, Validators.minLength(3), Validators.maxLength(40), Validators.pattern(/^[A-Za-zÁÉÍÓÚáéíóúñÑ0-9\s]*$/)]),
      tipoID: new FormControl(this.esUpdate ? this.data.cuenta.tipoID : '', [Validators.required]),
      numeroID: new FormControl(this.esUpdate ? this.data.cuenta.numeroID : '', [Validators.required, Validators.maxLength(15), Validators.minLength(7), Validators.pattern(/^[0-9]*$/)]),
      banco: new FormControl(this.esUpdate ? this.data.cuenta.banco : '', [Validators.required]),
      tipoCuenta: new FormControl(1),
      numeroCuenta: new FormControl(this.esUpdate ? this.data.cuenta.numeroCuenta : '', [
        Validators.required,
        Validators.maxLength(10),
        Validators.minLength(10),
        Validators.pattern(/^[0-9]+$/)
      ]),
    });
    this.initLists();
  }

  getNumeroIdErrorMessage(field: string): string | null {
    const numeroIDControl = this.formCuenta?.get(field);
    if (!numeroIDControl || !numeroIDControl.errors) return null;


    if (numeroIDControl?.hasError('maxlength')) {
      return 'Máximo de 15 caracteres.';
    }
    if (numeroIDControl?.hasError('minlength')) {
      return 'Mínimo 7 caracteres.';
    }
    if (numeroIDControl?.hasError('required')) {
      return 'Este campo es obligatorio.';
    }
    if (numeroIDControl?.hasError('pattern')) {
      return 'Solo números.';
    }

    return '';
  }


  getErrorMessageName(field: string): string | null {
    const control = this.formCuenta?.get(field);

    if (!control || !control.errors) return null;

    if (control?.hasError('maxlength')) {
      return 'Máximo 40 caracteres';
    }
    if (control?.hasError('minlength')) {
      return 'Mínimo 3 caracteres';
    }
    if (control?.hasError('required')) {
      return 'Este campo es obligatorio';
    }
    if (control?.hasError('pattern')) {
      return 'Sólo caracteres alfabéticos';
    }

    return null;
  }

  getErrorMessageAccountNumber(field: string): string | null {
    const control = this.formCuenta?.get(field);

    if (!control || !control.errors) return null;

    // Ya no se recorta lo que se pega, así que el mensaje dice cuánto sobra o
    // falta: «Máximo 20» no ayuda a quien pegó 21 dígitos sin darse cuenta.
    if (control?.hasError('maxlength') || control?.hasError('minlength')) {
      const largo = this.isPersonMovilPay ? 10 : 20;
      const diferencia = String(control.value || '').length - largo;
      const que = this.isPersonMovilPay ? 'El teléfono' : 'La cuenta';
      const cuantos = Math.abs(diferencia);
      const verbo = diferencia > 0 ? 'sobra' : 'falta';
      return cuantos === 1
        ? `${que} debe tener ${largo} dígitos: ${verbo} 1`
        : `${que} debe tener ${largo} dígitos: ${verbo}n ${cuantos}`;
    }
    if (control?.hasError('required')) {
      return 'Este campo es obligatorio';
    }
    if (control?.hasError('pattern')) {
      return 'Sólo caracteres numericos';
    }

    return null;
  }


}
